import { Popover } from '@headlessui/react';
import {
  CloudArrowUpIcon,
  CpuChipIcon,
  PrinterIcon,
  RectangleGroupIcon,
  Square2StackIcon,
} from '@heroicons/react/24/outline';
import clsx from 'clsx';

import { Spinner } from '@/components/ui/spinner';
import {
  SUMMARY_TOOL_MENU_XLS_SINGLE_COL as DSC, // drawing single column
  SUMMARY_TOOL_MENU_XLS_MULTIP_COL as DMC, // drawing multiple column
  SUMMARY_TOOL_MENU_PRJ_SINGLE_COL as PSC, // project single column
  SUMMARY_TOOL_MENU_PRJ_MULTIP_COL as PMC, // project multiple column
  SUMMARY_TOOL_MENU_ITEMIZED_EXPORT as ITX,
  SUMMARY_SEV_BY_DRAWING as SEVD,
  SUMMARY_SEV_BY_PROJECT as SEVP,
  EXPORTING,
  MARKED_UP_PRINT,
  VENDOR_FASTDUCT_PAGE,
  VENDOR_WENDES_PAGE,
  VENDOR_FASTDUCT_PROJECT,
  VENDOR_WENDES_PROJECT,
} from '@/config';

import {
  SUMMARY_XP_FORMAT as FMT,
  EXPORT_REPORT_SCALE as ERS,
  EXPORT_REPORT_LAYOUT as ERL,
  ExportScope,
} from '../types';

export type ExportFuncType = (
  format: string,
  /** for project or drawing */
  scale: string,
  layout?: string,
  group?: boolean,
  segment_size?: number,
) => void;

type ExportToolProps = {
  downloading?: boolean;
  exportTo: ExportFuncType;
  exportSEV: (scale: string) => void;
};

type ExportToVendorProps = {
  creating?: boolean;
  outputTo: (vendorId: string, format: string, scope: string) => void;
};

type PrintToolProps = {
  printing?: boolean;
  printLocal: () => void;
};

enum Vendor {
  F = 'fast_duct',
  W = 'wendes',
}

/**
 * Vendor integration used export menus
 * @date 2025/08/06
 */
const EstimatingTool = ({ outputTo }: ExportToVendorProps) => (
  <Popover className="relative">
    {/* disabled waiting for full implementation in backend - 2025/08/13 */}
    <Popover.Button className="btn-title-tool mr-2">
      <CpuChipIcon className="size-4 text-blue-600 inline-block" />
      <span className="text-sm px-1">Estimating API</span>
    </Popover.Button>

    <Popover.Panel className="absolute z-500 ">
      <ul className="btn-popup-menu w-44">
        <Popover.Button
          className="btn-popup-menu-item border-b border-gray-300 text-xs text-left p-2 pl-6"
          onClick={() => outputTo(Vendor.F, 'csv', ExportScope.PG)}
        >
          {VENDOR_FASTDUCT_PAGE}
        </Popover.Button>
        <Popover.Button
          className="btn-popup-menu-item border-b border-gray-300 text-xs text-left p-2 pl-6"
          onClick={() => outputTo(Vendor.W, 'csv', ExportScope.PG)}
        >
          {VENDOR_WENDES_PAGE}
        </Popover.Button>
        <Popover.Button
          className="btn-popup-menu-item border-b border-gray-300 text-xs text-left p-2 pl-6"
          onClick={() => outputTo(Vendor.F, 'csv', ExportScope.PJ)}
        >
          {VENDOR_FASTDUCT_PROJECT}
        </Popover.Button>
        <Popover.Button
          className="btn-popup-menu-item border-b border-gray-300 text-xs text-left p-2 pl-6"
          onClick={() => outputTo(Vendor.W, 'csv', ExportScope.PJ)}
        >
          {VENDOR_WENDES_PROJECT}
        </Popover.Button>
        {/* <li className="btn-popup-menu-item">Quotesoft</li> */}
      </ul>
    </Popover.Panel>
  </Popover>
);

const ExportDrawingTool = ({ exportTo, exportSEV }: ExportToolProps) => (
  <Popover className="relative">
    <Popover.Button className="btn-title-tool mr-2">
      <CloudArrowUpIcon className="size-4 text-blue-600 inline-block" />
      <span className="text-sm px-1">Export</span>
    </Popover.Button>

    <Popover.Panel className="absolute z-500 ">
      {/* this class determine the with of menu */}
      <ul className="btn-popup-menu w-52">
        {/* <Popover.Button
          className="btn-popup-menu-item border-b border-gray-300 text-xs text-left p-2"
          onClick={() => exportTo(FMT.C, ERS.D)}
        >
          {CSV}
        </Popover.Button> */}
        {/* multiple columns for project */}
        <Popover.Button
          className="btn-popup-menu-item border-b border-gray-300 text-xs text-left p-2"
          onClick={() => exportTo(FMT.E, ERS.P, ERL.M)}
        >
          {PMC}
        </Popover.Button>
        {/* multiple columns for page */}
        <Popover.Button
          className="btn-popup-menu-item border-b border-gray-300 text-xs text-left p-2"
          onClick={() => exportTo(FMT.E, ERS.D, ERL.M)}
        >
          {DMC}
        </Popover.Button>
        {/* single column for project */}
        <Popover.Button
          className="btn-popup-menu-item border-b border-gray-300 text-xs text-left p-2"
          onClick={() => exportTo(FMT.E, ERS.P, ERL.S)}
        >
          {PSC}
        </Popover.Button>
        {/* single column for page */}
        <Popover.Button
          className="btn-popup-menu-item border-b border-gray-300 text-xs text-left p-2"
          onClick={() => exportTo(FMT.E, ERS.D, ERL.S)}
        >
          {DSC}
        </Popover.Button>
        {/* itemized export - @2025/05/28 */}
        <Popover.Button
          className="btn-popup-menu-item border-b border-gray-300 text-xs text-left p-2"
          onClick={() => exportTo(FMT.E, ERS.D, ERL.M, false, 5)}
        >
          {ITX}
        </Popover.Button>
        {/* SEV export - @2025/11/14 */}
        <Popover.Button
          className="btn-popup-menu-item border-b border-gray-300 text-xs text-left p-2"
          onClick={() => exportSEV(ERS.D)}
        >
          {SEVD}
        </Popover.Button>
        <Popover.Button
          className="btn-popup-menu-item border-b border-gray-300 text-xs text-left p-2"
          onClick={() => exportSEV(ERS.P)}
        >
          {SEVP}
        </Popover.Button>
      </ul>
    </Popover.Panel>
  </Popover>
);

const LabelSpinner = ({ sizeClass = 'w-34' }: { sizeClass?: string }) => (
  <div className={clsx('h-30 mx-1', sizeClass)}>
    <Spinner size="sm" className="mt-1 inline-block" />
    <span className=" ml-1 text-xs inline-block text-gray-600 align-bottom">
      {EXPORTING}
    </span>
  </div>
);

export const TakeoffTopTools = ({
  downloading,
  creating,
  printing,
  togglePreview,
  exportTo,
  exportSEV,
  outputTo,
  printLocal,
}: {
  togglePreview: boolean;
} & ExportToolProps &
  ExportToVendorProps &
  PrintToolProps) => (
  <>
    {downloading && <LabelSpinner />}
    {!downloading && (
      <ExportDrawingTool exportTo={exportTo} exportSEV={exportSEV} />
    )}
    {/* == For vendor use such as: fast-duct - @2025/08/06 == */}
    {creating && <LabelSpinner />}
    {!creating && <EstimatingTool outputTo={outputTo} />}
    {/* == marked up exporting == */}
    {printing && <LabelSpinner sizeClass="w-38" />}
    {!printing && (
      <button
        type="button"
        className="btn-title-tool mr-2"
        onClick={printLocal}
      >
        <PrinterIcon className="size-4 text-blue-600 inline-block" />
        <span className="text-sm px-1">{MARKED_UP_PRINT}</span>
      </button>
    )}
    <button type="button" className="btn-title-tool mr-2 hidden">
      {togglePreview ? (
        <Square2StackIcon className="size-4 text-blue-600 inline-block" />
      ) : (
        <RectangleGroupIcon className="size-4 text-blue-600 inline-block" />
      )}
      <span className="text-sm px-1">
        {togglePreview ? 'Report' : 'Symbol'} View
      </span>
    </button>
  </>
);
